import { useCallback, useEffect, useState } from "react";
import { ReferralLeads } from "@/components/ReferralLeads";
import { ClientGroups } from "@/components/ClientGroups";
import { api, fmt, fmtDate, HL_KIND } from "@/lib/api";
import { ClientForm } from "@/components/ClientForm";
import { MetricForm } from "@/components/MetricForm";
import { SessionForm } from "@/components/SessionForm";
import { ProgressChart } from "@/components/ProgressChart";
import { SessionActions } from "@/components/SessionActions";
import { ClientTile } from "@/components/ClientTile";
import { Avatar } from "@/components/Avatar";
import { ProgressPhotos } from "@/components/ProgressPhotos";
import { AssignExercises } from "@/components/SessionNotes";
import { ClientWeek } from "@/components/ClientWeek";
import { VoicePlayer } from "@/components/VoiceRecorder";
import { ReactionBadge } from "@/components/Reactions";
import { FlagChip, StatusChip, Modal, Empty, Chip } from "@/components/ui-bits";

export default function Clients() {
  const [clients, setClients] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [modal, setModal] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [metricType, setMetricType] = useState("SQUAT_5RM");
  const [tab, setTab] = useState("overview");

  const loadList = useCallback(() => api.get("/clients", { params: { includeInactive: showInactive } }).then((r) => setClients(r.data)), [showInactive]);
  const loadDetail = useCallback(() => openId ? api.get(`/clients/${openId}`).then((r) => setDetail(r.data)) : setDetail(null), [openId]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  const refresh = () => { setModal(null); loadList(); loadDetail(); };

  const types = detail ? [...new Set(detail.metrics.map((m) => m.type))] : [];
  const mt = types.includes(metricType) ? metricType : types[0];
  const series = detail ? detail.metrics.filter((m) => m.type === mt).slice().reverse() : [];

  return (
    <>
    <div className="panel fade" data-testid="admin-clients">
      <div className="ph">
        <h3>Clients · {clients?.length ?? 0} {showInactive ? "total" : "active"} — tap a photo to open the profile, hover for the camera to upload</h3>
        <div className="actions">
          <button className="btn g sm" onClick={() => setShowInactive(!showInactive)} data-testid="toggle-inactive-button">{showInactive ? "Hide inactive" : "Show inactive"}</button>
          <button className="btn sm" onClick={() => setModal("add")} data-testid="add-client-button">+ New client</button>
        </div>
      </div>
      {!clients ? <div className="center"><span className="spin" /></div> : (
        <div className="tiles" data-testid="client-grid">
          {clients.map((c) => <ClientTile key={c.id} client={c} onOpen={(cl) => { setOpenId(cl.id); setTab("overview"); }} onPhotoChanged={refresh} />)}
          {clients.length === 0 && <Empty>No clients yet — add your first one.</Empty>}
        </div>
      )}

      {openId && (
        <Modal title={detail?.name?.toUpperCase() || "CLIENT"} onClose={() => setOpenId(null)} testId="client-detail-modal" wide>
          {!detail ? <div className="center" style={{ minHeight: 200 }}><span className="spin" /></div> : (
            <div data-testid="client-detail">
              <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 12 }}>
                <Avatar client={detail} size={84} radius={14} />
                <div style={{ flex: 1 }}>
                  <div className="cn" style={{ fontSize: 17 }} data-testid="client-detail-name">{detail.name}</div>
                  <div className="ss">{detail.user.email} · {detail.user.phone || "no phone"}</div>
                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}><FlagChip flag={detail.flag} />{!detail.active && <Chip cls="bad" label="Inactive" />}</div>
                </div>
                <div className="actions" style={{ flexDirection: "column", alignItems: "stretch" }}>
                  <button className="btn sm g" onClick={() => setModal("edit")} data-testid="edit-client-button">Edit profile</button>
                  <button className="btn sm g" onClick={() => setModal("metric")} data-testid="log-metric-button">Log metric</button>
                  <button className="btn sm g" onClick={() => setModal("session")} data-testid="add-client-session-button">Add session</button>
                </div>
              </div>
              <div className="tabs">
                {[["overview", "Overview"], ["progress", "Progress"], ["sessions", "Sessions"], ["exercises", "Exercises"], ["reports", "Reports"], ["messages", "Messages"]].map(([k, l]) =>
                  <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)} data-testid={`detail-tab-${k}`}>{l}</button>)}
              </div>

              {tab === "overview" && (
                <div data-testid="detail-overview">
                  <ClientWeek detail={detail} onChanged={loadDetail} />
                  <div className="kv"><span>Goal</span><span>{detail.goal || "—"}</span></div>
                  <div className="kv"><span>This week</span><span data-testid="client-week-summary">{detail.weekSummary}</span></div>
                  <div className="kv"><span>Avg adherence</span><span className="num">{detail.adherence}%</span></div>
                  <div className="kv"><span>Cadence</span><span>{detail.sessionsPerWeek}x/week · {(detail.preferredTimes || []).join(", ")}</span></div>
                  <div className="kv"><span>Preferred channel</span><span>{detail.preferredChannel}{detail.motivationOn ? " · motivation on" : ""}</span></div>
                  <div className="kv"><span>Injuries</span><span style={{ textAlign: "right" }} data-testid="client-injuries-list">{detail.injuries?.length ? <span className="hl" style={{ justifyContent: "flex-end" }}>{detail.injuries.map((t) => <span className="injury" key={t}>{t}</span>)}</span> : "—"}</span></div>
                  <div className="kv"><span>Private notes</span><span style={{ textAlign: "right" }}>{detail.notes || "—"}</span></div>
                  <div className="kv"><span>Client since</span><span>{fmtDate(detail.createdAt, { day: "numeric", month: "short", year: "numeric" })}</span></div>
                </div>
              )}
              {tab === "progress" && (
                <div data-testid="detail-progress">
                  <div className="tabs">{types.map((t) => <button key={t} className={t === mt ? "on" : ""} onClick={() => setMetricType(t)} data-testid={`metric-tab-${t}`}>{t.replace("_", " ")}</button>)}</div>
                  <ProgressChart values={series.map((m) => m.value)} unit={series[0]?.unit || ""} />
                  <table className="tbl"><tbody>
                    {series.slice().reverse().slice(0, 10).map((m) => <tr key={m.id}><td className="num">{fmtDate(m.recordedAt)}</td><td className="num"><b data-testid={`metric-row-${m.id}`}>{m.value}{m.unit}</b></td><td className="ss">{m.note || ""}</td></tr>)}
                  </tbody></table>
                  {series.length === 0 && <Empty>No metrics logged yet.</Empty>}
                  <h3 style={{ fontSize: 13, margin: "16px 0 6px" }}>Progress photos</h3>
                  <ProgressPhotos photos={detail.progressPhotos} uploadPath={`/clients/${detail.id}/progress-photos`} onChanged={loadDetail} />
                </div>
              )}
              {tab === "sessions" && (
                <div data-testid="detail-sessions">
                  {detail.sessions.map((s) => (
                    <div className="sess" key={s.id} style={{ padding: "6px 0" }} data-testid={`client-session-${s.id}`}>
                      <div className="t num" style={{ width: 120, fontSize: 12 }}>{fmt(s.startAt)}</div>
                      <div className="sb"><div className="ss" style={{ color: "var(--ink)" }}>{s.title}{s.recoveryTotal != null ? <span className="ss num"> · recovery {s.recoveryDone}/{s.recoveryTotal}</span> : null}</div></div>
                      <StatusChip status={s.status} />
                      <SessionActions session={s} admin onDone={refresh} />
                    </div>
                  ))}
                  {detail.sessions.length === 0 && <Empty>No sessions yet.</Empty>}
                </div>
              )}
              {tab === "exercises" && <div data-testid="detail-exercises"><AssignExercises clientId={detail.id} programId={detail.programId} onProgramChanged={loadDetail} /></div>}
              {tab === "reports" && (
                <div data-testid="detail-reports">
                  {detail.reports.map((r) => (
                    <div className="rep" key={r.id} style={{ alignItems: "flex-start" }}>
                      <div className="rd num">{fmtDate(r.weekStart)}</div>
                      <div style={{ flex: 1, fontSize: 12 }}>{r.summary}
                        <div className="hl">{(r.highlights || []).map((h, i) => <Chip key={i} cls={HL_KIND[h.kind]?.[0]} label={h.text} />)}</div>
                        {r.coachNote && <div className="note" style={{ cursor: "default" }}><b>Coach note:</b> {r.coachNote}</div>}</div>
                      <div className="rbar"><i style={{ width: `${r.adherence}%` }} /></div>
                      <div className="rv num">{r.adherence}%</div>
                    </div>
                  ))}
                  {detail.reports.length === 0 && <Empty>No reports yet.</Empty>}
                </div>
              )}
              {tab === "messages" && (
                <div data-testid="detail-messages">
                  {detail.messages.map((m) => (
                    <div className={`msg ${m.direction === "INBOUND" ? "in" : ""}`} key={m.id}>
                      <div className="who"><span>{m.direction === "INBOUND" ? "← client" : "→ coach"} · {m.channel}</span><span>{fmt(m.createdAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>
                      <div className="txt">{m.body}</div>
                      {m.voiceNoteId && <VoicePlayer id={m.voiceNoteId} testId={`detail-message-voice-${m.id}`} />}
                      <ReactionBadge message={m} testId={`detail-message-reaction-${m.id}`} />
                    </div>
                  ))}
                  {detail.messages.length === 0 && <Empty>No messages yet.</Empty>}
                </div>
              )}
              <div className="mfoot"><button className="btn g" onClick={() => setOpenId(null)} data-testid="client-detail-close">Close</button></div>
            </div>
          )}
        </Modal>
      )}
      {modal === "add" && <ClientForm onClose={() => setModal(null)} onSaved={(c) => { setOpenId(c.id); refresh(); }} />}
      {modal === "edit" && detail && <ClientForm client={detail} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === "metric" && detail && <MetricForm clientId={detail.id} onClose={() => setModal(null)} onSaved={() => { setTab("progress"); refresh(); }} />}
      {modal === "session" && detail && <SessionForm clientId={detail.id} onClose={() => setModal(null)} onSaved={() => { setTab("sessions"); refresh(); }} />}
    </div>
    <ClientGroups clients={clients || []} />
    <ReferralLeads onConverted={(cid) => { loadList(); setOpenId(cid); setTab("overview"); }} />
    </>
  );
}
